/**
 * Documentos imprimibles Neocoop — comprobantes, welcome cards, etc.
 *
 * Sigue el sistema de diseño documentado en `docs/neocoop-design-language.md`
 * y los helpers de `printableStyles.ts`. La estructura visual siempre es:
 *   1) Header brand con isotipo pattern wallpaper
 *   2) Hero overlapping con el protagonista
 *   3) Body con monto + key-value minimal
 *   4) Footer legal de 1 línea
 *
 * Implementación: HTML + CSS abierto en window.open() con print() automático.
 */
import {
  printableBaseCss,
  printableToolbarCss,
  printableCardCss,
  printableHeaderCss,
  printableHeroCss,
  printableKvCss,
  printableLegalCss,
  printableHeader,
  printableHero,
  printableLegal,
  printableCopyScript,
  printableDownloadScript,
} from './printableStyles'

// `printableLegal` lo usa el welcome card (próxima iteración). El comprobante
// requiere autorización INGECOP obligatoria así que arma su footer custom.
void printableLegal

// ============================================================
// Resoluciones INGECOP — autorizaciones oficiales por tipo de documento
// ============================================================

/** RIG · Recibo de Ingresos */
export const INGECOP_AUTORIZACION_INGRESOS =
  'AUTORIZADO SEGÚN RESOLUCIÓN: INGECOP IGC-C-SALF-RESF-1943-2025 DEL 01 AL 1,000,000 DE FECHA 10 DE DICIEMBRE DE 2025'

/** REG · Recibo de Egresos */
export const INGECOP_AUTORIZACION_EGRESOS =
  'AUTORIZADO SEGÚN RESOLUCIÓN: IGC-C-SALF-RESF-1944-2025 DEL 01 AL 1,000,000 DE FECHA 10 DE DICIEMBRE DE 2025'

/** CDP · Certificado de Depósito a Plazo Fijo */
export const INGECOP_AUTORIZACION_CDP =
  'AUTORIZADO SEGÚN RESOLUCIÓN: INGECOP IGC-C-SALF-RESF-1945-2025 DEL 01 AL 10,000 DE FECHA 10 DE DICIEMBRE DE 2025'

// ============================================================
// Tipos
// ============================================================

export type ReciboData = {
  serie: string
  correlativo: number
  fecha: string                    // ISO
  asociado_no: string              // legacy interno (2026-000045)
  neo_id?: string                  // identidad social real (482619). Si no viene se deriva.
  asociado_nombre: string
  asociado_dpi: string
  concepto: string
  desglose?: Array<{ label: string; monto: number }>
  monto: number
  forma_pago: string
  cajero: string
  banco?: string
  numero_cuenta?: string
  numero_boleta?: string
  fecha_boleta?: string
  /**
   * Token aleatorio (16 hex chars) impreso en el QR del comprobante.
   * Lo exige la edge function `verificar-documento` como mitigación
   * anti-enumeración. Si no viene, se imprime el comprobante sin QR
   * (mejor sin QR que con uno que no valida).
   */
  verification_code?: string
}

/** Clase del recibo — decide título, autorización INGECOP, copy y filename. */
type ReciboClase = 'INGRESO' | 'EGRESO'

export type WelcomeCardData = {
  asociado_no: string
  neo_id?: string                  // identidad social real (482619). Si no viene se deriva.
  asociado_nombre: string
  fecha_ingreso: string
}

// ============================================================
// COMPROBANTE — ticket vertical mobile-first (estilo Cash App / Wise)
// ============================================================
//
// Una sola función parametrizada por clase. Cambia entre RIG/REG:
//  - Título header               ("Comprobante de Ingreso" | "...Egreso")
//  - Label del monto             ("Monto recibido"          | "Monto entregado")
//  - Etiqueta del bloque banco   ("Cuenta origen"           | "Cuenta destino")
//  - Autorización INGECOP        (RIG 1943-2025             | REG 1944-2025)
//  - Filename del PNG            ("Comprobante-Ingreso-..." | "Comprobante-Egreso-...")
//
// Lo demás (hero, neo.id, QR, footer) es idéntico — la identidad
// visual del documento es la misma serie de la cooperativa.

export function imprimirReciboIngresos(d: ReciboData): void {
  imprimirReciboInterno(d, 'INGRESO')
}

export function imprimirReciboEgresos(d: ReciboData): void {
  imprimirReciboInterno(d, 'EGRESO')
}

function imprimirReciboInterno(d: ReciboData, clase: ReciboClase): void {
  const isIngreso = clase === 'INGRESO'
  const titulo = isIngreso ? 'Comprobante de Ingreso' : 'Comprobante de Egreso'
  const labelMonto = isIngreso ? 'Monto recibido' : 'Monto entregado'
  const labelBanco = isIngreso ? 'Cuenta origen' : 'Cuenta destino'
  const autorizacion = isIngreso ? INGECOP_AUTORIZACION_INGRESOS : INGECOP_AUTORIZACION_EGRESOS
  const filenamePrefix = isIngreso ? 'Comprobante-Ingreso' : 'Comprobante-Egreso'
  // Distintivos visuales para que se identifique la clase de un vistazo
  // (cuando se comparte por WhatsApp en miniatura)
  const bandaLabel = isIngreso ? 'INGRESO' : 'EGRESO'
  const estadoLabel = isIngreso ? 'Confirmado' : 'Entregado'
  const estadoVariant = isIngreso ? 'ok' : 'brand'
  const signoMonto = isIngreso ? '+' : '−'
  // neo.id — identidad social del miembro. Preferimos el real (asignado por
  // generar_neo_id en migración 0023). Si no viene, fallback al derivado del
  // legacy no_asociado para no romper en QA.
  const neoId = d.neo_id ? formatNeoIdRaw(d.neo_id) : formatNeoId(d.asociado_no)
  // Texto plano del neo.id para copiar al clipboard (sin "NEO" prefix ni espacios)
  const neoIdRaw = (d.neo_id ?? d.asociado_no.replace(/\D/g, '').slice(-6).padStart(6, '0'))
  // Tipo de operación legible para mostrar en el comprobante
  // ('INSCRIPCIÓN ASOCIADO' es un caso compuesto exclusivo de ingresos —
  //  inscripción = cuota de ingreso + aportación inicial)
  const tipoMov = (isIngreso && d.desglose && d.desglose.length > 1)
    ? 'INSCRIPCIÓN ASOCIADO'
    : (d.concepto || '').toUpperCase()
  // Forma de pago en uppercase para alinear con el resto
  const formaPagoTxt = formaPagoLabelYColor(d.forma_pago).label.toUpperCase()
  // Referencia interna del comprobante: serie alias + año + correlativo
  const correlativoTxt = String(d.correlativo).padStart(4, '0')
  const yearTxt = new Date(d.fecha).getFullYear()
  const referencia = `${shortSerie(d.serie)}-${yearTxt}-${correlativoTxt}`
  // URL del QR para verificación digital.
  // Formato 3 segmentos: /r/{correlativo}/{verification_code}. El code
  // (16 hex) es obligatorio en la edge function pública para evitar
  // enumeración por correlativo. Si no llega lo dejamos vacío para que
  // el debug en QA sea obvio (el QR caerá en "no encontrado").
  const correlativoQr = String(d.correlativo).padStart(6, '0')
  const qrPayload = d.verification_code
    ? `https://neocoop.gt/r/${correlativoQr}/${d.verification_code}`
    : `https://neocoop.gt/r/${correlativoQr}`
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=4&data=${encodeURIComponent(qrPayload)}`
  // Fecha + hora compacta tipo "19/01/2026 • 13:45"
  const fechaHora = formatFechaHoraCompacta(d.fecha)

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Comprobante · Neocoop</title>
<style>
  @page { size: 420px 880px; margin: 0; }

  /* === Sistema de diseño Neocoop === */
  ${printableBaseCss()}
  ${printableToolbarCss(388)}
  ${printableCardCss(388)}
  ${printableHeaderCss()}
  ${printableHeroCss()}
  ${printableKvCss()}
  ${printableLegalCss()}

  /* === Específico del comprobante === */

  /* BODY */
  .body { padding: 26px 28px 14px; }

  /* MONTO */
  .monto-row {
    display: flex; justify-content: space-between; align-items: baseline;
    margin-bottom: 4px;
  }
  .monto-row .label {
    font-size: 10px; font-weight: 700;
    color: var(--muted); letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .monto-row .estado {
    font-size: 9.5px; font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    display: inline-flex; align-items: center; gap: 4px;
  }
  .monto-row .estado::before {
    content: ''; width: 6px; height: 6px;
    border-radius: 50%;
  }
  .monto-row .estado.is-ok    { color: var(--ok-ink); }
  .monto-row .estado.is-ok::before    { background: var(--ok-dot); }
  .monto-row .estado.is-brand { color: var(--brand-deep, #c43c00); }
  .monto-row .estado.is-brand::before { background: var(--brand, #FF5B04); }

  /* Signo prefijo del monto (+ verde para ingresos, − ink-soft para egresos)
     Sutil — el dato fuerte sigue siendo el monto, el signo es contexto. */
  .monto .signo {
    font-size: 32px; font-weight: 700;
    margin-right: 4px;
    letter-spacing: -1px;
  }
  .monto .signo.is-ingreso { color: var(--ok-ink); }
  .monto .signo.is-egreso  { color: var(--brand-deep, #c43c00); }

  /* SELLO DIAGONAL clásico de recibo contable guatemalteco — esquina sup. derecha.
     Va dentro de .card-neocoop (que tiene overflow:hidden) para que el rotado
     quede contenido limpio. */
  .banda-sello {
    position: absolute;
    top: 22px;
    right: -50px;
    transform: rotate(45deg);
    transform-origin: center;
    padding: 5px 60px;
    font-size: 10px; font-weight: 800;
    letter-spacing: 3px;
    color: #fff;
    text-align: center;
    z-index: 3;
    pointer-events: none;
    box-shadow: 0 2px 6px rgba(0,0,0,0.18);
  }
  .banda-sello.is-ingreso {
    background: linear-gradient(135deg, var(--ok-ink, #15803d) 0%, #14532d 100%);
  }
  .banda-sello.is-egreso {
    background: linear-gradient(135deg, var(--brand-deep, #c43c00) 0%, #7a2400 100%);
  }
  .card-neocoop { position: relative; }
  .monto {
    font-size: 40px; font-weight: 800;
    color: var(--ink);
    letter-spacing: -1.4px;
    font-feature-settings: "tnum";
    line-height: 1;
  }
  .monto .moneda {
    font-size: 14px; font-weight: 600;
    color: var(--muted);
    margin-left: 6px;
  }

  /* DESGLOSE (cuando hay 2+ conceptos) */
  .desglose {
    margin-top: 14px;
    padding: 12px 14px;
    background: var(--soft-gray);
    border-radius: 10px;
    font-size: 12px;
  }
  .desglose .r {
    display: flex; justify-content: space-between;
    padding: 3px 0;
    color: var(--ink-soft);
  }
  .desglose .r .v { color: var(--ink); font-weight: 600; font-feature-settings: "tnum"; }

  /* DETALLE BANCARIO (origen para INGRESO, destino para EGRESO) */
  .dep-detail {
    background: var(--soft-gray);
    padding: 10px 12px;
    border-radius: 8px;
    margin-top: 6px;
    font-size: 11px; color: var(--ink-soft);
  }
  .dep-detail-header {
    font-size: 9px; font-weight: 700;
    color: var(--muted); letter-spacing: 1.4px;
    text-transform: uppercase;
    padding-bottom: 4px;
    border-bottom: 1px dashed var(--hairline);
    margin-bottom: 4px;
    display: flex; justify-content: flex-start;
  }
  .dep-detail > div:not(.dep-detail-header) { display: flex; justify-content: space-between; padding: 2px 0; }
  .dep-detail span:first-child { color: var(--muted); }
  .dep-detail span:last-child { font-weight: 600; color: var(--ink); }

  /* QR — alineado a la izquierda con texto a la derecha estilo Mercury */
  .qr-block {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 28px;
    border-top: 1px dashed var(--hairline);
  }
  .qr-block img {
    width: 78px; height: 78px;
    display: block;
    flex-shrink: 0;
  }
  .qr-block .qr-text {
    flex: 1; min-width: 0;
  }
  .qr-block .qr-text .qr-label {
    font-size: 11px; font-weight: 700;
    color: var(--ink); letter-spacing: 0.3px;
  }
  .qr-block .qr-text .qr-hint {
    font-size: 10px; color: var(--muted);
    margin-top: 4px;
    line-height: 1.45;
  }

  /* Footer extendido cuando legalmente debe ir la autorización INGECOP */
  .neo-legal .ingecop {
    margin-top: 8px;
    font-size: 7.5px;
    color: var(--muted);
    line-height: 1.5;
    letter-spacing: 0.15px;
    font-weight: 400;
  }

  @media print {
    body { background: #fff; padding: 0; min-height: 0; align-items: stretch; }
    .ticket { box-shadow: none; border-radius: 0; }
    .toolbar { display: none; }
  }
</style>
</head>
<body>
  <div>
    <div class="toolbar">
      <span class="hint">Compartilo por WhatsApp o imprimilo</span>
      <button class="primary" data-action="download-png">
        <span class="btn-label">Descargar imagen</span>
      </button>
      <button onclick="window.print()">Imprimir / PDF</button>
    </div>
    <div class="card-neocoop" id="downloadable-card">
      <!-- Sello diagonal clásico recibo contable — distingue ingreso vs egreso de un vistazo -->
      <div class="banda-sello is-${isIngreso ? 'ingreso' : 'egreso'}">${bandaLabel}</div>
      ${printableHeader(titulo)}

      ${printableHero({
        eyebrow: 'Asociado',
        headline: d.asociado_nombre,
        ref: neoId,
        refCopyValue: neoIdRaw,
      })}

      <div class="body">
        <div class="monto-row">
          <div class="label">${labelMonto}</div>
          <div class="estado is-${estadoVariant}">${estadoLabel}</div>
        </div>
        <div class="monto"><span class="signo is-${isIngreso ? 'ingreso' : 'egreso'}">${signoMonto}</span>Q ${formatMonto(d.monto)}<span class="moneda">GTQ</span></div>

        ${d.desglose && d.desglose.length > 1 ? `
        <div class="desglose">
          ${d.desglose.map((it) => `
            <div class="r">
              <span>${escapeHtml(it.label)}</span>
              <span class="v">Q ${formatMonto(it.monto)}</span>
            </div>
          `).join('')}
        </div>
        ` : ''}

        <hr class="neo-hr-dash" />

        <div class="neo-kv"><span class="k">Operación</span><span class="v">${escapeHtml(tipoMov)}</span></div>
        <div class="neo-kv"><span class="k">Forma de pago</span><span class="v">${escapeHtml(formaPagoTxt)}</span></div>
        ${d.banco ? `
        <div class="dep-detail">
          <div class="dep-detail-header">${labelBanco}</div>
          ${d.banco ? `<div><span>Banco</span><span>${escapeHtml(d.banco)}</span></div>` : ''}
          ${d.numero_cuenta ? `<div><span>Cuenta</span><span>${escapeHtml(d.numero_cuenta)}</span></div>` : ''}
          ${d.numero_boleta ? `<div><span>N° boleta</span><span>${escapeHtml(d.numero_boleta)}</span></div>` : ''}
          ${d.fecha_boleta ? `<div><span>Fecha boleta</span><span>${escapeHtml(formatFechaCorta(d.fecha_boleta))}</span></div>` : ''}
        </div>` : ''}
        <div class="neo-kv"><span class="k">Fecha</span><span class="v">${escapeHtml(fechaHora)}</span></div>
        <div class="neo-kv"><span class="k">Referencia</span><span class="v mono copyable" data-copy="${escapeHtml(referencia)}" title="Click para copiar">${escapeHtml(referencia)}</span></div>
      </div>

      <!-- QR a la izquierda — sin URL expuesta, escaneable para verificar -->
      <div class="qr-block">
        <img src="${qrSrc}" alt="QR verificación" />
        <div class="qr-text">
          <div class="qr-label">Verificá tu pago</div>
          <div class="qr-hint">Escaneá para abrir este comprobante online o reenviarlo por WhatsApp.</div>
        </div>
      </div>

      <!-- Footer legal con autorización INGECOP obligatoria -->
      <div class="neo-legal">
        <strong>NEOCOOP R.L.</strong> · NIT 120719290 ·
        <div class="ingecop">${escapeHtml(autorizacion)}</div>
      </div>
    </div>
  </div>
  <script>
    ${printableCopyScript()}
    ${printableDownloadScript(`${filenamePrefix}-NEO-${neoIdRaw}-${correlativoTxt}.png`)}
  </script>
</body>
</html>`

  abrirVentana(html, `Comprobante ${d.correlativo}`, 460)
}

// ============================================================
// PASE DE BIENVENIDA — carnet horizontal estilo tarjeta de débito
// ============================================================

export function imprimirWelcomeCard(d: WelcomeCardData): void {
  // neo.id real preferido, fallback al derivado del legacy
  const neoIdRaw = (d.neo_id ?? d.asociado_no.replace(/\D/g, '').slice(-6).padStart(6, '0'))
  const neoIdDisplay = `NEO ${neoIdRaw.slice(0, 3)} ${neoIdRaw.slice(3, 6)}`
  const nombreUpper = d.asociado_nombre.toUpperCase()
  const miembroDesde = formatFechaCardholder(d.fecha_ingreso)
  const primerNombreUpper = (d.asociado_nombre.trim().split(/\s+/)[0] ?? '').toUpperCase()

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Bienvenido · ${escapeHtml(neoIdDisplay)}</title>
<style>
  /* Tarjeta horizontal proporción débito/crédito real (1.586:1) */
  @page { size: 856px 540px; margin: 0; }
  :root {
    --brand:       #FF5B04;
    --brand-deep:  #c43c00;
    --brand-glow:  #ff7a30;
    --ink:         #14171c;
    --muted:       #9aa0a6;
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Noto Sans', 'Inter', system-ui, -apple-system, Segoe UI, sans-serif;
    background: #e9eaec;
    min-height: 100vh;
    display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
    padding: 36px 16px;
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .toolbar { width: 720px; margin: 0 auto 16px; display: flex; gap: 6px; justify-content: flex-end; align-items: center; }
  .toolbar .hint { font-size: 11px; color: var(--muted); margin-right: auto; }
  .toolbar button {
    background: #fff; color: var(--ink);
    border: 1px solid var(--hairline);
    padding: 8px 14px; font-size: 12px; font-weight: 600;
    border-radius: 999px; cursor: pointer; font-family: inherit;
    letter-spacing: 0.2px;
    transition: all 120ms ease;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .toolbar button:hover { border-color: var(--ink); }
  .toolbar button.primary {
    background: var(--brand); color: #fff; border-color: var(--brand);
  }
  .toolbar button.primary:hover { background: var(--brand-deep); border-color: var(--brand-deep); }
  .toolbar button:disabled { opacity: 0.5; cursor: wait; }

  /* ============================================================
     TARJETA — proporción tarjeta de débito real, brand sólido
     720 × 454 (1.586:1)
     v2: rediseñado más limpio. Background brand sólido (sin
     radial-gradient que confundía contraste) + glare diagonal sutil.
     Sin pattern de isotipos en el fondo (interfería con el texto).
     ============================================================ */
  .card {
    width: 720px;
    aspect-ratio: 1.586 / 1;
    background-color: #FF5B04;
    background-image: linear-gradient(135deg, #FF7A30 0%, #FF5B04 45%, #C43C00 100%);
    border-radius: 22px;
    color: #fff;
    padding: 40px 44px 36px;
    position: relative;
    overflow: hidden;
    box-shadow:
      0 30px 70px rgba(255, 91, 4, 0.30),
      0 8px 18px rgba(0, 0, 0, 0.10);
    display: flex; flex-direction: column; justify-content: space-between;
  }

  /* Glare diagonal sutil (aporta dimensionalidad sin restar contraste) */
  .card::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(118deg, transparent 35%, rgba(255,255,255,0.12) 50%, transparent 65%);
    pointer-events: none;
  }

  /* TOP — logo + saludo con nombre */
  .row-top {
    display: flex; align-items: flex-start; justify-content: space-between;
    position: relative; z-index: 1;
  }
  .row-top img.logo {
    height: 30px; width: auto;
    display: block;
    filter: brightness(0) invert(1);
  }
  .row-top .saludo {
    text-align: right;
  }
  .row-top .saludo .hi {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.85;
    letter-spacing: 0.3px;
  }
  .row-top .saludo .name {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.4px;
    margin-top: 2px;
    text-shadow: 0 1px 4px rgba(0,0,0,0.15);
  }

  /* MID — frase emocional concisa, sin quote-mark decorativo que hacía
     ruido visual contra el wallpaper. Hard limit en el ancho para que
     respire. */
  .frase {
    position: relative; z-index: 1;
    max-width: 380px;
  }
  .frase .text {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.42;
    letter-spacing: -0.15px;
    text-shadow: 0 1px 3px rgba(0,0,0,0.12);
  }
  .frase .text strong {
    font-weight: 700;
  }

  /* BOTTOM — neo.id (PAN) + cardholder + valid thru */
  .row-bottom {
    position: relative; z-index: 1;
  }
  .neo-id {
    font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 5px;
    text-shadow: 0 2px 6px rgba(0,0,0,0.18);
    line-height: 1;
  }
  .footer-info {
    margin-top: 16px;
    display: flex; justify-content: space-between; align-items: flex-end;
    gap: 16px;
  }
  .footer-info .info-block .label {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    opacity: 0.7;
  }
  .footer-info .info-block .value {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-top: 4px;
    text-shadow: 0 1px 3px rgba(0,0,0,0.10);
  }
  .footer-info .cardholder { flex: 1; min-width: 0; }
  .footer-info .cardholder .value {
    text-transform: uppercase;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .footer-info .since { text-align: right; flex-shrink: 0; }
  .footer-info .since .value {
    font-family: 'IBM Plex Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 1.2px;
  }

  /* Caption discreto bajo la tarjeta */
  .caption {
    width: 720px;
    margin-top: 18px;
    text-align: center;
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 0.2px;
    line-height: 1.6;
  }
  .caption strong { color: var(--ink); font-weight: 700; }

  @media print {
    body { background: #fff; padding: 0; min-height: 0; }
    .card { box-shadow: none; border-radius: 0; }
    .toolbar { display: none; }
    .caption { display: none; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <span class="hint">Listo para compartir</span>
    <button class="primary" data-action="download-png">
      <span class="btn-label">Descargar imagen</span>
    </button>
    <button onclick="window.print()">Imprimir / PDF</button>
  </div>

  <div class="card" id="downloadable-card">
    <!-- TOP — logo + saludo con nombre -->
    <div class="row-top">
      <img class="logo" src="/brand/logo-horizontal.png" alt="Neocoop" />
      <div class="saludo">
        <div class="hi">Bienvenido,</div>
        <div class="name">${escapeHtml(primerNombreUpper)}.</div>
      </div>
    </div>

    <!-- MID — frase emocional concisa -->
    <div class="frase">
      <div class="text">
        Hoy comienza <strong>tu historia con Neocoop</strong>.<br/>
        La primera cooperativa 100% digital, hecha para crecer con vos.
      </div>
    </div>

    <!-- BOTTOM — neo.id como PAN + cardholder + miembro desde -->
    <div class="row-bottom">
      <div class="neo-id">${escapeHtml(neoIdDisplay)}</div>
      <div class="footer-info">
        <div class="info-block cardholder">
          <div class="label">Asociado</div>
          <div class="value">${escapeHtml(nombreUpper)}</div>
        </div>
        <div class="info-block since">
          <div class="label">Miembro desde</div>
          <div class="value">${escapeHtml(miembroDesde)}</div>
        </div>
      </div>
    </div>
  </div>

  <p class="caption">
    <strong>Nos enorgullece llamarte socio.</strong> Compartí esta tarjeta donde quieras —
    es tu acceso oficial a Neocoop.
  </p>

  <script>
    ${printableDownloadScript(`Bienvenido-${neoIdRaw}.png`)}
  </script>
</body>
</html>`

  abrirVentana(html, `Bienvenido ${neoIdDisplay}`, 800)
}

/** Fecha como "ABR 2026" — formato cardholder valid-thru */
function formatFechaCardholder(iso: string): string {
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00-06:00' : iso)
  const month = d.toLocaleDateString('es-GT', { month: 'short', timeZone: 'America/Guatemala' }).toUpperCase().replace('.', '')
  const year = d.toLocaleDateString('es-GT', { year: 'numeric', timeZone: 'America/Guatemala' })
  return `${month} ${year}`
}

// ============================================================
// Helpers internos
// ============================================================

function abrirVentana(html: string, titulo: string, anchoVentana = 760): void {
  const w = window.open('', '_blank', `width=${anchoVentana},height=900`)
  if (!w) {
    alert('El navegador bloqueó la ventana emergente. Habilitá pop-ups para ' + titulo)
    return
  }
  w.document.open()
  w.document.write(html)
  w.document.close()
  w.document.title = titulo
}

function escapeHtml(s: string | null | undefined): string {
  if (!s) return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatFechaCorta(iso: string): string {
  // Columnas DATE ('YYYY-MM-DD') se anclan a mediodía GT: new Date() puro
  // las parsea como medianoche UTC y en GT imprime el día ANTERIOR.
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00-06:00' : iso)
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guatemala' })
}

function formatHora(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
}
void formatHora

function formatMonto(v: number): string {
  return v.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function getIniciales(nombre: string): string {
  const parts = nombre.trim().split(/\s+/)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
void getIniciales

function getPrimerNombre(nombre: string): string {
  const first = nombre.trim().split(/\s+/)[0] ?? ''
  return first.charAt(0) + first.slice(1).toLowerCase()
}
void getPrimerNombre

function formatNumeroSocio(no: string): string {
  const clean = no.replace(/\D/g, '').padStart(12, '0')
  return clean.match(/.{1,4}/g)?.join('  ') ?? no
}
void formatNumeroSocio

/**
 * Fallback legacy — derivado del no_asociado (`2026-000045` → NEO 000 045).
 * Se usa solo cuando el asociado todavía no tiene neo_id real (QA pre-0023).
 */
function formatNeoId(noAsociado: string): string {
  const digits = noAsociado.replace(/\D/g, '').slice(-6).padStart(6, '0')
  return `NEO ${digits.slice(0, 3)} ${digits.slice(3, 6)}`
}

/**
 * Formato canónico del neo.id real (ya viene como 6 dígitos plano del backend).
 */
function formatNeoIdRaw(neoId: string): string {
  const clean = neoId.replace(/\D/g, '').padStart(6, '0').slice(-6)
  return `NEO ${clean.slice(0, 3)} ${clean.slice(3, 6)}`
}

/** DPI guatemalteco 13 dígitos formateado 4-5-4 → '2081 58890 1401' */
function formatDpiLegible(dpi: string): string {
  if (!dpi) return ''
  const clean = dpi.replace(/\D/g, '')
  if (clean.length !== 13) return dpi
  return `${clean.slice(0, 4)} ${clean.slice(4, 9)} ${clean.slice(9, 13)}`
}
void formatDpiLegible

/** 19/01/2026 • 13:45 */
function formatFechaHoraCompacta(iso: string): string {
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} • ${hh}:${mi}`
}

/** Toma alias corto de la serie INGECOP (los 3 chars antes del año si existe) */
function shortSerie(serie: string): string {
  // 'IGC-C-SALF-RESF-1943-2025' → 'RIG' o algún anchor corto
  // Por ahora extraemos el primer alias de 3 letras o usamos 'RIG' default
  const match = serie.match(/[A-Z]{3,4}/)
  return match?.[0] ?? 'RIG'
}

/**
 * Formato neurociencia para el N° de asociado:
 * Chunks 4-3-3 separados por middot.
 *   '2026-000042' → '2026 · 000 042'
 * Aplica Miller's Law (chunks de 3 dígitos = 2 items mentales en lugar de 11
 * caracteres sueltos), elimina el guion (frecuente confusión al dictar)
 * y separa visualmente año del correlativo.
 */
function formatSocioChunks(no: string): string {
  const clean = no.replace(/\D/g, '')
  if (clean.length === 10) {
    // 2026000042 → '2026 · 000 042'
    return `${clean.slice(0, 4)} · ${clean.slice(4, 7)} ${clean.slice(7, 10)}`
  }
  // Fallback: si viene con guion, lo respetamos pero separamos chunks de 3
  if (no.includes('-')) {
    const [yr, num] = no.split('-')
    if (yr && num) {
      const padded = num.padStart(6, '0')
      return `${yr} · ${padded.slice(0, 3)} ${padded.slice(3, 6)}`
    }
  }
  return no
}
void formatSocioChunks

function prettyFormaPago(t: string): string {
  const map: Record<string, string> = {
    efectivo: 'Efectivo',
    deposito: 'Depósito bancario',
    cheque: 'Cheque',
    transferencia_tercero: 'Transferencia de tercero',
    transferencia_bancaria: 'Transferencia bancaria',
  }
  return map[t] ?? t
}
void prettyFormaPago

function formaPagoSvg(t: string): string {
  const stroke = 'currentColor'
  switch (t) {
    case 'efectivo':
      return `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/></svg>`
    case 'deposito':
      return `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>`
    default:
      return `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l-5-5 5-5M2 12h13M17 7l5 5-5 5M22 12H9"/></svg>`
  }
}

// Pill compacto al estilo Banrural — color sutil que codifica el tipo
function formaPagoLabelYColor(t: string): { label: string; bg: string; color: string } {
  switch (t) {
    case 'efectivo':
      return { label: 'Efectivo en caja', bg: '#dcfce7', color: '#166534' }
    case 'deposito':
      return { label: 'Depósito bancario', bg: '#dbeafe', color: '#1e40af' }
    case 'cheque':
      return { label: 'Cheque', bg: '#fef3c7', color: '#92400e' }
    case 'transferencia_tercero':
      return { label: 'Transferencia de socio', bg: '#ede9fe', color: '#5b21b6' }
    case 'transferencia_bancaria':
      return { label: 'Transferencia bancaria', bg: '#fce7f3', color: '#9d174d' }
    case 'debito_ahorro':
      return { label: 'Débito de cuenta de ahorro', bg: '#e0f2fe', color: '#075985' }
    default:
      return { label: t, bg: '#f3f4f6', color: '#374151' }
  }
}

function formatFechaLarga(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'long', year: 'numeric' })
}
void formatFechaLarga

// Mark used to avoid unused-warning when iconos quedan reservados
void formaPagoSvg
